import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Users, ChefHat, Wrench, Leaf, ArrowRight, Quote, Camera } from "lucide-react";
import SEO from "../components/SEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0drtr76p_c2c.png";

// Placeholder images for the homepage - replace with actual photos when available
const communityImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/qsaagn1e_Youth%203.jpg",
    alt: "Landscaping Division Team",
    caption: "Landscaping Division Team"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/7vumo687_Seniors%201.jpg",
    alt: "Senior Lunch Program",
    caption: "Senior Lunch Program"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/w6w7juat_Youth%205.jpg",
    alt: "Landscaping Project",
    caption: "Landscaping in Action"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0hdhafgk_Seniors%205.jpg",
    alt: "Valentine's Day Celebration",
    caption: "Senior Celebrations"
  },
];

const programs = [
  {
    icon: ChefHat,
    title: "C2C Culinary",
    description: "Youth cooking programs, community meals, and culinary skills training.",
    image: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0hdhafgk_Seniors%205.jpg",
  },
  {
    icon: Wrench,
    title: "Construction Training",
    description: "Hands-on construction and carpentry skills for real-world job readiness.",
    image: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0l894l17_Youth%207.jpg",
  },
  {
    icon: Leaf,
    title: "Landscaping Skills",
    description: "Learn landscaping, gardening, and environmental stewardship.",
    image: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/4q3g0kvb_Youth%206.jpg",
  },
  {
    icon: Users,
    title: "Youth Leadership",
    description: "Building tomorrow's leaders through mentorship and skill development.",
    image: "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/qsaagn1e_Youth%203.jpg",
  },
];

const stats = [
  { number: "500+", label: "Youth Served" },
  { number: "1000+", label: "Meals Delivered" },
  { number: "50+", label: "Community Partners" },
  { number: "100%", label: "Heart & Dedication" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Home"
        description="Confidence 2 Create Inc. — Educating youth, feeding seniors, and bridging generations in Saint Paul, MN. #EverybodyEats"
      />
      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0l894l17_Youth%207.jpg" 
            alt="C2C Youth at Work" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0033A0]/85" />
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#FFC107] rounded-full filter blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <span className="inline-block bg-[#FFC107] text-[#0033A0] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Building Stronger Communities
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 leading-tight">
                Confidence 2<br />
                <span className="text-[#FFC107]">Create</span> Inc.
              </h1>
              <p className="text-lg md:text-2xl text-white/90 mb-6 md:mb-8 leading-relaxed max-w-lg">
                Educating and closing the gap between generations, bringing unity back into our communities.
              </p>
              <p className="text-2xl md:text-3xl font-bold text-[#FFC107] italic mb-8 md:mb-10">#Everybody eats</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/donations"
                  data-testid="hero-donate-btn"
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  <Heart size={20} />
                  Donate Now
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-learn-more-btn"
                  className="bg-white/10 backdrop-blur text-white hover:bg-white/20 px-8 py-3 rounded-full font-bold transition-all flex items-center justify-center gap-2 border border-white/30"
                >
                  Learn More
                  <ArrowRight size={18} />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#FFC107] rounded-full blur-2xl opacity-30 transform scale-110" />
                <img
                  src={LOGO_URL}
                  alt="Confidence 2 Create Logo"
                  className="relative w-80 h-80 object-contain"
                  data-testid="hero-logo"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 -mt-16 relative z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-3xl md:text-5xl font-black text-[#0033A0] mb-2">
                  {stat.number}
                </div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community In Action - Photo Gallery Section */}
      <section className="section-padding bg-white" data-testid="community-photos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Camera className="w-6 h-6 text-[#FFC107]" />
              <span className="text-[#FFC107] font-bold tracking-wide uppercase">Community In Action</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0033A0] mt-4">
              See What We Do
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {communityImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-2xl aspect-square"
                data-testid={`community-image-${index}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0033A0]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-bold">{image.caption}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/gallery"
              data-testid="view-gallery-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              View Full Gallery
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-slate-50" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-8 md:mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Our Mission</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0033A0] mt-4 mb-4 md:mb-6">
              It Takes a Village
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              C2C is about serving and investing where we live and work. We're bridging the gap and reviving a lost tradition of community support, teaching youth lifelong skills while creating meaningful connections between generations.
            </p>
          </motion.div>

          {/* Programs Grid with Images */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:border-[#FFC107] transition-all duration-300 hover:shadow-lg group card-hover overflow-hidden"
                data-testid={`program-card-${index}`}
              >
                <div className="h-48 overflow-hidden m-3 rounded-xl border-2 border-slate-200 group-hover:border-[#FFC107] transition-colors">
                  <img 
                    src={program.image} 
                    alt={program.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#0033A0] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FFC107] transition-colors -mt-10 relative z-10 shadow-lg">
                    <program.icon className="w-6 h-6 text-white group-hover:text-[#0033A0] transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0033A0] mb-3">{program.title}</h3>
                  <p className="text-slate-600">{program.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Quote Section with Background Image */}
      <section className="section-padding relative overflow-hidden" data-testid="quote-section">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/fequoqe5_Seniors%202.jpg" 
            alt="Community Elders Event" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0033A0]/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <Quote className="w-10 h-10 md:w-16 md:h-16 text-[#FFC107] mx-auto mb-6 md:mb-8 opacity-50" />
          <motion.blockquote
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-lg md:text-3xl text-white font-medium leading-relaxed mb-6 md:mb-8"
          >
            "All initiatives will provide an ongoing forum for local entrepreneurs, leaders and other adults who believe in the importance of 'each one, teach one' and are willing to share their knowledge with our youth."
          </motion.blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="w-16 h-16 bg-[#FFC107] rounded-full flex items-center justify-center">
              <span className="text-[#0033A0] font-black text-xl">NM</span>
            </div>
            <div className="text-left">
              <p className="text-white font-bold">Neeko McLemore</p>
              <p className="text-[#FFC107]">Founder & President</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#0033A0] to-[#1E40AF] rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC107] rounded-full filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <h2 className="text-2xl md:text-5xl font-bold text-white mb-4 md:mb-6">
                Ready to Make a Difference?
              </h2>
              <p className="text-base md:text-xl text-white/90 mb-8 md:mb-10 max-w-2xl mx-auto">
                Whether you want to volunteer, donate, or enroll a youth in our programs, we'd love to hear from you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/donations"
                  data-testid="cta-donate-btn"
                  className="btn-secondary text-lg"
                >
                  <Heart className="inline mr-2" size={20} />
                  Donate Now
                </Link>
                <Link
                  to="/contact"
                  data-testid="cta-contact-btn"
                  className="bg-white text-[#0033A0] hover:bg-slate-100 px-8 py-3 rounded-full font-bold transition-all"
                >
                  Get Involved
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
